import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Check, Loader2, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";
import { PageHeader } from "@/components/landing/PageHeader";
import { Footer } from "@/components/landing/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  "Define manpower requirements", "Candidate sourcing", "CV screening", "Experience verification",
  "Interview coordination", "Candidate shortlisting", "Documentation support",
  "Work-permit / immigration coordination", "Pre-departure support",
];

const EMPTY = { company_name: "", contact_email: "", contact_phone: "", title: "", category: "General", country: "", salary: "", accommodation: "", eligibility: "International candidates welcome", max_applicants: "" };
const CATS = ["Construction", "Warehousing", "Manufacturing", "Hospitality", "Transport", "Housekeeping", "Skilled Trades", "Agriculture", "General"];

export default function EmployersPage() {
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const s = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.company_name || !f.contact_email || !f.title || !f.country || !f.salary) {
      toast.error("Please fill company, email, job title, country and salary."); return;
    }
    setLoading(true);
    try {
      const payload = { ...f };
      if (payload.max_applicants === "") delete payload.max_applicants;
      else payload.max_applicants = parseInt(payload.max_applicants, 10);
      const res = await axios.post(`${API}/employer/post-job`, payload);
      toast.success(res.data.message || "Your job is now live.");
      setF(EMPTY);
    } catch (err) { toast.error(err.response?.data?.detail || "Something went wrong."); }
    finally { setLoading(false); }
  };

  const input = "w-full bg-transparent border-b border-[#e4e4e7] py-3 font-body text-base text-[#0a0a0a] placeholder:text-[#a1a1aa] focus:outline-none focus:border-[#ff3b30] transition-colors";

  return (
    <main data-testid="employers-page">
      <Toaster position="bottom-right" theme="light" toastOptions={{ style: { background: "#f4f4f5", border: "1px solid #e4e4e7", color: "#0a0a0a", borderRadius: "0" } }} />
      <PageHeader />
      <section className="pt-16 md:pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-[1600px] mx-auto">
          <Reveal>
            <p className="font-body text-sm tracking-[0.25em] uppercase text-[#ff3b30] mb-6">For employers</p>
            <h1 className="font-head uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl xl:text-8xl">Post a role.<br />Go live instantly.</h1>
            <p className="mt-8 font-body text-lg text-[#52525b] max-w-2xl leading-relaxed">Tell us your manpower requirement. Your role goes live immediately on our candidates page, where verified candidates can apply directly.</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-8 px-6 md:px-12">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-x-10">
          {STEPS.map((it, i) => (
            <Reveal key={it} delay={(i % 3) * 0.05}>
              <div className="flex items-start gap-3 py-4 border-b border-[#e4e4e7] font-body text-[#0a0a0a]">
                <Check size={18} className="text-[#ff3b30] shrink-0 mt-0.5" strokeWidth={2.5} />{it}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-16 px-6 md:px-12">
        <div className="max-w-[1000px] mx-auto">
          <Reveal>
            <form onSubmit={submit} data-testid="post-job-form" className="bg-white border border-[#e4e4e7] p-8 md:p-12 space-y-8">
              <h2 className="font-head uppercase tracking-tight text-3xl">Post your job requirement</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input data-testid="pj-company" className={input} placeholder="Company name *" value={f.company_name} onChange={s("company_name")} />
                <input data-testid="pj-email" type="email" className={input} placeholder="Contact email *" value={f.contact_email} onChange={s("contact_email")} />
                <input data-testid="pj-phone" className={input} placeholder="WhatsApp / Phone" value={f.contact_phone} onChange={s("contact_phone")} />
                <input data-testid="pj-title" className={input} placeholder="Job title *" value={f.title} onChange={s("title")} />
                <select data-testid="pj-category" className={input} value={f.category} onChange={s("category")}>
                  {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input data-testid="pj-country" className={input} placeholder="Country *" value={f.country} onChange={s("country")} />
                <input data-testid="pj-salary" className={input} placeholder="Salary (e.g. €800 – €1,200 / mo) *" value={f.salary} onChange={s("salary")} />
                <input data-testid="pj-accom" className={input} placeholder="Accommodation" value={f.accommodation} onChange={s("accommodation")} />
                <input data-testid="pj-eligibility" className={`${input} md:col-span-2`} placeholder="Eligibility (e.g. International candidates welcome)" value={f.eligibility} onChange={s("eligibility")} />
                <input data-testid="pj-max-applicants" type="number" min="1" className={input} placeholder="Max applicants (optional — listing closes when full)" value={f.max_applicants} onChange={s("max_applicants")} />
              </div>
              <button type="submit" disabled={loading} data-testid="post-job-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#ff3b30] hover:bg-[#ff5248] disabled:opacity-60 text-white font-head uppercase tracking-wide px-8 py-4 font-600 transition-colors">
                {loading ? <>Publishing <Loader2 size={18} className="animate-spin" /></> : "Publish job — go live"}
              </button>
              <p className="font-body text-xs text-[#a1a1aa]">Your posting appears instantly on the candidates page. Our team also reviews it for verification.</p>
            </form>
            <Link to="/candidates" className="mt-8 inline-flex items-center gap-2 font-head uppercase text-sm tracking-wide text-[#ff3b30]">View live candidate job page <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>
      <Footer />
    </main>
  );
}
